import React from 'react'
import { Button } from '@/components/ui/button'
import { Plus, Search, FolderOpen } from 'lucide-react'
import { HomeWidgetShell } from '@/components/dashboard/home-widget-shell'
import { testIds } from '@/lib/test-ids'

export type WorkspaceBrowseTarget = {
  workspaceId: string
  name: string
  documentsRootPath: string
}

interface QuickActionsSectionProps {
  onNewDocument?: () => void
  onSearch?: () => void
  browseTargets?: WorkspaceBrowseTarget[]
  onBrowseWorkspace?: (documentsRootPath: string) => void
}

export function QuickActionsSection({
  onNewDocument,
  onSearch,
  browseTargets = [],
  onBrowseWorkspace,
}: QuickActionsSectionProps) {
  type QuickAction = {
    icon: typeof Plus
    label: string
    onClick: () => void
    title: string
    shortcut?: string
  }

  const browseActions: QuickAction[] = browseTargets
    .filter((t) => t.documentsRootPath)
    .map((t) => ({
      icon: FolderOpen,
      label: `Browse ${t.name}`,
      onClick: () => onBrowseWorkspace?.(t.documentsRootPath),
      title: `Open Documents for ${t.name}`,
    }))

  const actions: QuickAction[] = [
    {
      icon: Plus,
      label: 'New Document',
      onClick: () => onNewDocument?.(),
      title: 'New document',
    },
    {
      icon: Search,
      label: 'Search',
      onClick: () => onSearch?.(),
      shortcut: '⌘K',
      title: 'Search (⌘K)',
    },
    ...browseActions,
  ]

  return (
    <HomeWidgetShell title="Quick Actions" data-testid={testIds.home.quickActions}>
      {browseTargets.length > 0 && (
        <p className="text-xs text-muted-foreground mb-3">
          Browse opens the Documents tree for each enabled workspace.
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        {actions.map((action) => {
          const Icon = action.icon
          return (
            <Button
              key={action.label}
              variant="secondary"
              size="sm"
              className="h-8 gap-1.5"
              onClick={action.onClick}
              title={
                action.title ??
                (action.shortcut ? `${action.label} (${action.shortcut})` : action.label)
              }
            >
              <Icon className="h-3.5 w-3.5 shrink-0" />
              <span className="text-xs">{action.label}</span>
            </Button>
          )
        })}
      </div>
    </HomeWidgetShell>
  )
}
